import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { A2UIResponse } from '../types/a2ui';

// Fallback UUID generator for non-secure contexts (HTTP)
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  html?: string;
  a2ui?: A2UIResponse;
  data?: Record<string, unknown>;
  timestamp: number;
  isStreaming?: boolean;
}

export interface ActiveFilter {
  key: string;
  value: string;
  label: string;
}

export interface QueryHistoryItem {
  id: string;
  query: string;
  timestamp: number;
  starred?: boolean;
}

export interface SavedView {
  id: string;
  name: string;
  description?: string;
  timestamp: number;
  starred?: boolean;
  // Snapshot of visualization state
  query: string;
  a2ui: A2UIResponse | null;
  data: Record<string, unknown>;
  filters: ActiveFilter[];
  dateRange: DateRange;
}

export type DateRangePreset = 'today' | '7d' | '30d' | '90d' | 'all' | 'custom';

export interface DateRange {
  preset: DateRangePreset;
  startDate: string | null; // ISO date string
  endDate: string | null; // ISO date string
}

interface ConversationState {
  messages: Message[];
  currentHTML: string | null;
  currentA2UI: A2UIResponse | null;
  currentData: Record<string, unknown>;
  contextData: Record<string, unknown>;
  isGenerating: boolean;
  pendingQuery: string | null;
  activeFilters: ActiveFilter[];
  queryHistory: QueryHistoryItem[];
  dateRange: DateRange;
  savedViews: SavedView[];

  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  setVisualization: (visualization: { html?: string | null; a2ui?: A2UIResponse | null; data?: Record<string, unknown> }) => void;
  setContextData: (key: string, data: unknown) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  clearConversation: () => void;
  submitQuery: (query: string) => void;
  clearPendingQuery: () => void;
  setActiveFilters: (filters: ActiveFilter[]) => void;
  removeFilter: (key: string) => void;
  clearFilters: () => void;
  addToHistory: (query: string) => void;
  toggleStarred: (id: string) => void;
  clearHistory: () => void;
  setDateRange: (range: DateRange) => void;
  setDateRangePreset: (preset: DateRangePreset) => void;
  // Saved views
  saveView: (name: string, description?: string) => void;
  loadView: (id: string) => void;
  deleteView: (id: string) => void;
  toggleViewStarred: (id: string) => void;
}

// Helper to calculate date range from preset
function getDateRangeFromPreset(preset: DateRangePreset): { startDate: string | null; endDate: string | null } {
  const now = new Date();
  const endDateStr = now.toISOString().split('T')[0] || null; // Today

  switch (preset) {
    case 'today':
      return { startDate: endDateStr, endDate: endDateStr };
    case '7d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { startDate: start.toISOString().split('T')[0] || null, endDate: endDateStr };
    }
    case '30d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { startDate: start.toISOString().split('T')[0] || null, endDate: endDateStr };
    }
    case '90d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 90);
      return { startDate: start.toISOString().split('T')[0] || null, endDate: endDateStr };
    }
    case 'all':
    default:
      return { startDate: null, endDate: null };
  }
}

const useConversationStore = create<ConversationState>()(
  persist(
    (set) => ({
      messages: [],
      currentHTML: null,
      currentA2UI: null,
      currentData: {},
      contextData: {},
      isGenerating: false,
      pendingQuery: null,
      activeFilters: [],
      queryHistory: [],
      dateRange: { preset: 'all', startDate: null, endDate: null },
      savedViews: [],

      addMessage: (message) => {
        const id = generateUUID();
        const newMessage: Message = {
          ...message,
          id,
          timestamp: Date.now(),
        };
        set((state) => ({
          messages: [...state.messages, newMessage],
        }));
        return id;
      },

      updateMessage: (id, updates) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === id ? { ...msg, ...updates } : msg
          ),
        }));
      },

      setVisualization: ({ html, a2ui, data = {} }) => set({
        currentHTML: html ?? null,
        currentA2UI: a2ui ?? null,
        currentData: data,
      }),

      setContextData: (key, data) =>
        set((state) => ({
          contextData: { ...state.contextData, [key]: data },
        })),

      setIsGenerating: (isGenerating) => set({ isGenerating }),

      clearConversation: () =>
        set({
          messages: [],
          currentHTML: null,
          currentA2UI: null,
          currentData: {},
          contextData: {},
          isGenerating: false,
          pendingQuery: null,
          activeFilters: [],
        }),

      submitQuery: (query: string) => set({ pendingQuery: query }),

      clearPendingQuery: () => set({ pendingQuery: null }),

      setActiveFilters: (filters: ActiveFilter[]) => set({ activeFilters: filters }),

      removeFilter: (key: string) =>
        set((state) => ({
          activeFilters: state.activeFilters.filter((f) => f.key !== key),
        })),

      clearFilters: () => set({ activeFilters: [] }),

      addToHistory: (query: string) =>
        set((state) => {
          // Avoid duplicates - if the query already exists, move it to top
          const existing = state.queryHistory.find(
            (h) => h.query.toLowerCase() === query.toLowerCase()
          );
          let newHistory: QueryHistoryItem[];
          if (existing) {
            newHistory = [
              { id: existing.id, query: existing.query, timestamp: Date.now(), starred: existing.starred },
              ...state.queryHistory.filter((h) => h.id !== existing.id),
            ];
          } else {
            newHistory = [
              { id: generateUUID(), query, timestamp: Date.now() },
              ...state.queryHistory,
            ];
          }
          // Keep only last 20 queries
          return { queryHistory: newHistory.slice(0, 20) };
        }),

      toggleStarred: (id: string) =>
        set((state) => ({
          queryHistory: state.queryHistory.map((h) =>
            h.id === id ? { ...h, starred: !h.starred } : h
          ),
        })),

      clearHistory: () => set({ queryHistory: [] }),

      setDateRange: (range: DateRange) => set({ dateRange: range }),

      setDateRangePreset: (preset: DateRangePreset) => {
        const { startDate, endDate } = getDateRangeFromPreset(preset);
        set({ dateRange: { preset, startDate, endDate } });
      },

      // Saved views actions
      saveView: (name: string, description?: string) =>
        set((state) => {
          // Only save if there's a current visualization
          if (!state.currentA2UI) return state;

          // Find the last user query that generated this visualization
          const lastUserMessage = [...state.messages].reverse().find((m) => m.role === 'user');
          const query = lastUserMessage?.content || '';

          const newView: SavedView = {
            id: generateUUID(),
            name,
            description,
            timestamp: Date.now(),
            query,
            a2ui: state.currentA2UI,
            data: state.currentData,
            filters: state.activeFilters,
            dateRange: state.dateRange,
          };

          return { savedViews: [newView, ...state.savedViews] };
        }),

      loadView: (id: string) =>
        set((state) => {
          const view = state.savedViews.find((v) => v.id === id);
          if (!view) return state;

          return {
            currentA2UI: view.a2ui,
            currentData: view.data,
            activeFilters: view.filters,
            dateRange: view.dateRange,
          };
        }),

      deleteView: (id: string) =>
        set((state) => ({
          savedViews: state.savedViews.filter((v) => v.id !== id),
        })),

      toggleViewStarred: (id: string) =>
        set((state) => ({
          savedViews: state.savedViews.map((v) =>
            v.id === id ? { ...v, starred: !v.starred } : v
          ),
        })),
    }),
    {
      name: 'llm-dashboard-conversation',
      partialize: (state) => ({
        messages: state.messages.slice(-50), // Keep last 50 messages
        currentHTML: state.currentHTML,
        currentA2UI: state.currentA2UI,
        currentData: state.currentData,
        queryHistory: state.queryHistory.slice(0, 20), // Keep last 20 queries
        dateRange: state.dateRange, // Persist date range
        savedViews: state.savedViews.slice(0, 50), // Keep up to 50 saved views
      }),
    }
  )
);

interface ConversationContextValue extends ConversationState {
  sendMessage: (content: string) => Promise<string>;
}

const ConversationContext = createContext<ConversationContextValue | undefined>(undefined);

export function ConversationProvider({ children }: { children: React.ReactNode }) {
  const store = useConversationStore();

  // sendMessage is now just a simple wrapper that components can override
  // The actual LLM integration happens in ChatPanel using useLLM hook
  const sendMessage = useCallback(async (content: string) => {
    // Add user message
    store.addMessage({ role: 'user', content });

    // Add placeholder for assistant response
    const assistantId = store.addMessage({
      role: 'assistant',
      content: '',
      isStreaming: true,
    });

    store.setIsGenerating(true);

    // Return the assistant message ID so callers can update it
    return assistantId;
  }, [store]);

  const value = useMemo<ConversationContextValue>(
    () => ({
      ...store,
      sendMessage,
    }),
    [store, sendMessage]
  );

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversation() {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
}
